import { useEffect, useRef, useState } from "react";
import type { RendererParams } from "../../shared/api-contract";
import { useViewer } from "../../state/ViewerProvider";

/** How often an open export panel re-reads the renderer's output size. */
const POLL_MS = 1500;

/**
 * Export width/height that FOLLOWS the RENDERER section's output size.
 *
 * There were three independent size controls on one screen — the left Export
 * panel, the right Image Export section, and RENDERER itself — so setting
 * "Custom 2000 x 2000" drove none of the exports. The renderer is the single
 * source now and both panels default from it.
 *
 * Read straight off `getRendererParams` rather than through the editor store.
 * The store route worked for the right panel (which mounts after the value is
 * already there) but not for the left one (which is mounted first and needs the
 * UPDATE), and a size control that works in one panel and silently not in the
 * other is worse than no automation at all. A poll on an open panel is one
 * trivial RPC; the store already poll-refreshes `animation` far harder.
 *
 * Two deliberate limits:
 *
 *  - **Custom mode only.** In "full" mode the renderer reports the live canvas
 *    (~1512 x 696), and defaulting a print export to that would silently shrink
 *    it from the panel's own 1920 x 1080. Full mode keeps the panel default.
 *  - **Only until touched.** Once someone types a size into the field, that
 *    panel stops following, so a deliberate one-off export is never clobbered
 *    by a later renderer change. The other panel keeps following.
 */
export function useExportSize(defaultW: number, defaultH: number): {
  w: number;
  h: number;
  setW: (v: number) => void;
  setH: (v: number) => void;
  /** True while the size is still mirroring RENDERER — for a hint in the UI. */
  following: boolean;
} {
  const { client } = useViewer();
  const ring = client.ring;
  const [p, setP] = useState<RendererParams | null>(null);
  const [w, setWRaw] = useState(defaultW);
  const [h, setHRaw] = useState(defaultH);
  // Ref AND state: the ref is read inside the poll callback (which must see the
  // change immediately, without waiting for a render), the state is what the
  // "following" hint renders from — a ref alone left the hint claiming to follow
  // after the user had already taken the field over.
  const touched = useRef(false);
  const [touchedUi, setTouchedUi] = useState(false);

  useEffect(() => {
    let alive = true;
    const read = () => {
      // Stop asking once the user has taken the field over.
      if (touched.current) return;
      void ring.getRendererParams()
        .then((next) => { if (alive) setP(next); })
        .catch(() => { /* viewer not ready; keep the panel default */ });
    };
    read();
    const id = window.setInterval(read, POLL_MS);
    return () => { alive = false; window.clearInterval(id); };
  }, [ring]);

  const custom = p?.mode === "custom" && p.width > 0 && p.height > 0;

  useEffect(() => {
    if (touched.current || !custom || !p) return;
    setWRaw(p.width);
    setHRaw(p.height);
  }, [custom, p]);

  return {
    w,
    h,
    setW: (v: number) => { touched.current = true; setTouchedUi(true); setWRaw(v); },
    setH: (v: number) => { touched.current = true; setTouchedUi(true); setHRaw(v); },
    following: !!custom && !touchedUi,
  };
}
